/**
 * linkifyPostContent
 * Renders post text with a safe inline-markdown subset, plus #hashtags and
 * @mentions. Everything is rendered as React elements (text children are
 * escaped by React), so there is no HTML injection / XSS risk.
 *
 * Supported: **bold**, *italic*, `code`, http(s) links, #tag -> /tag/:tag,
 * @user -> /u/:username. Line breaks are preserved by the container's
 * white-space: pre-wrap. Block formatting is intentionally out of scope.
 */

import React from 'react';
import { Link } from 'react-router-dom';

const TOKEN = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`|https?:\/\/[^\s]+|#[A-Za-z0-9_]+|@[A-Za-z0-9_]+)/g;

/**
 * Split content into typed tokens. Pure + tested (no JSX) so the parsing has a check.
 */
export function tokenizePostContent(text) {
  if (!text) return [];
  const tokens = [];
  for (const part of text.split(TOKEN)) {
    if (!part) continue;
    if (/^\*\*[^*\n]+\*\*$/.test(part)) tokens.push({ type: 'bold', value: part.slice(2, -2) });
    else if (/^\*[^*\n]+\*$/.test(part)) tokens.push({ type: 'italic', value: part.slice(1, -1) });
    else if (/^`[^`\n]+`$/.test(part)) tokens.push({ type: 'code', value: part.slice(1, -1) });
    else if (/^https?:\/\/[^\s]+$/.test(part)) tokens.push({ type: 'url', value: part });
    else if (/^#[A-Za-z0-9_]+$/.test(part)) tokens.push({ type: 'hashtag', value: part.slice(1) });
    else if (/^@[A-Za-z0-9_]+$/.test(part)) tokens.push({ type: 'mention', value: part.slice(1) });
    else tokens.push({ type: 'text', value: part });
  }
  return tokens;
}

export function linkifyPostContent(text) {
  return tokenizePostContent(text).map((t, i) => {
    switch (t.type) {
      case 'bold':
        return <strong key={i}>{t.value}</strong>;
      case 'italic':
        return <em key={i}>{t.value}</em>;
      case 'code':
        return <code key={i} className="post-code">{t.value}</code>;
      case 'url':
        return (
          <a key={i} href={t.value} target="_blank" rel="noopener noreferrer" className="post-link">
            {t.value}
          </a>
        );
      case 'hashtag':
        return (
          <Link key={i} to={`/tag/${t.value.toLowerCase()}`} className="hashtag">
            #{t.value}
          </Link>
        );
      case 'mention':
        return (
          <Link key={i} to={`/u/${t.value}`} className="mention">
            @{t.value}
          </Link>
        );
      default:
        return t.value;
    }
  });
}
